import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, CalendarCheck, Star } from "lucide-react";
import { PHONE_SMS, PHONE_TEL } from "./nav";
import { openBooking } from "./booking/BookingDrawerHost";
import { setAttribution } from "../lib/metaEvent";
import { WinningWord } from "./sections";
import "./site.css";

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_LIGHT = "/brand/hello-mortgage-team-logo-footer.png";
const UTM = (href, id) => (href.startsWith("/") ? `${href}${href.includes("?") ? "&" : "?"}utm_source=instagram&utm_medium=bio&utm_content=${id}` : href);

function tap(button, label) {
  let sid = null;
  try { sid = sessionStorage.getItem("hm_sid"); } catch {}
  fetch(`${API}/api/public/start/tap`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ button, label, sid }), keepalive: true }).catch(() => {});
  try { if (window.fbq) window.fbq("trackCustom", "BioClick", { button, label }); } catch {}
}

function openBook(e) {
  e.preventDefault();
  setAttribution({ utm_source: "instagram", utm_medium: "bio", utm_content: "call" });  // the drawer never changes the URL, so tag it by hand
  openBooking({ path: "/start/" });  // team-voice widget; the page doesn't imply an intent so it opens on the chooser
}

const QUIPS = [
  [5, 12, "Morning.", "Mortgages before your morning coffee — brave."],
  [12, 17, "Afternoon lunch-break scroll?", "Nice to meet you, and your sandwich."],
  [17, 22, "", "couch scroller. Congrats. You made it out of the rabbit hole."],
];
function quip(h) {
  const q = QUIPS.find(([a, b]) => h >= a && h < b);
  return q ? { greet: q[2], line: q[3] } : { greet: "Late night?", line: "Winning doesn't sleep. Neither do you, apparently." };
}

function Card({ x, extra, ...rest }) {
  return (
    <blockquote {...rest}>
      <span className="start-quote" aria-hidden="true">“</span>
      <p>{x.text}</p>
      <footer>
        <strong>— {x.name}{x.location ? `, ${x.location}` : ""}</strong>
        <span className="start-stars" aria-label="5 stars on Google">{[0, 1, 2, 3, 4].map((k) => <Star key={k} size={13} fill="currentColor" strokeWidth={0} />)}<small>Google review</small></span>
      </footer>
      {extra}
    </blockquote>
  );
}

function Reviews({ items }) {
  const [i, setI] = useState(0);
  const [drag, setDrag] = useState(0);
  const [leaving, setLeaving] = useState(null);
  const [dir, setDir] = useState(1);
  const start = React.useRef(null);
  const go = (n, d = 1) => { const next = ((n % items.length) + items.length) % items.length; if (next === i) return; setDir(d); setLeaving(items[i]); setI(next); };
  useEffect(() => {
    if (items.length < 2) return;
    const t = setInterval(() => { setDir(1); setLeaving(items[i]); setI((i + 1) % items.length); }, 7000);
    return () => clearInterval(t);
  }, [items, i]);
  if (!items.length) return null;
  const r = items[i];
  const onDown = (e) => { start.current = e.clientX; e.currentTarget.setPointerCapture?.(e.pointerId); };
  const onMove = (e) => { if (start.current !== null) setDrag(e.clientX - start.current); };
  const onUp = () => { if (start.current === null) return; if (Math.abs(drag) > 50) go(i + (drag < 0 ? 1 : -1), drag < 0 ? 1 : -1); start.current = null; setDrag(0); };
  return (
    <div className="start-slider" data-testid="start-review-slider">
      <div className={`start-stage ${dir < 0 ? "back" : ""}`}>
        <Card x={r} key={r.id} className="start-review start-entering" data-testid="start-review" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} style={drag ? { transform: `translateX(${drag}px) rotate(${drag / 80}deg)`, transition: "none", animation: "none" } : undefined} />
        {leaving && <div className="start-leavewrap" key={`leave-${leaving.id}`} onAnimationEnd={() => setLeaving(null)}><Card x={leaving} className="start-review start-leaving" aria-hidden="true" data-testid="start-review-leaving" /></div>}
      </div>
      {items.length > 1 && (
        <div className="start-dots" role="tablist" aria-label="More reviews">
          {items.map((x, k) => <button key={x.id} type="button" role="tab" aria-selected={k === i} aria-label={`Review ${k + 1}`} className={k === i ? "on" : ""} onClick={() => go(k)} data-testid={`start-review-dot-${k}`} />)}
        </div>
      )}
    </div>
  );
}

function Headline({ text }) {
  const m = /winning/i.exec(text || "");
  if (!m) return text;
  return <>{text.slice(0, m.index)}<em><WinningWord word={m[0]} storageKey="hm_win_seen_start" /></em>{text.slice(m.index + m[0].length)}</>;
}

function Btn({ b, rate, i }) {
  const common = { className: "start-btn", style: { animationDelay: `${120 + i * 60}ms` }, "data-testid": `start-btn-${b.id}`, onClick: (e) => { tap(b.id, b.label); if (b.kind === "calendly") openBook(e); } };
  const sub = b.kind === "rates" && rate ? <>5-yr fixed from <em>{rate}%</em> today. Live Alberta rates.</> : b.sub || null;
  const body = (
    <>
      <span className="start-btn-text"><b>{b.label}</b>{sub && <small>{sub}</small>}</span>
      <span className="start-btn-arrow" aria-hidden="true"><ArrowRight size={18} /></span>
    </>
  );
  if (b.kind === "calendly") return <a href={UTM("/book/", b.id)} {...common}>{body}</a>;
  if (b.kind === "sms") return <a href={PHONE_SMS} {...common}>{body}</a>;
  if (b.kind === "tel") return <a href={PHONE_TEL} {...common}>{body}</a>;
  if (!b.href.startsWith("/")) return <a href={b.href} target="_blank" rel="noopener noreferrer" {...common}>{body}</a>;
  return <Link to={UTM(b.href, b.id)} {...common}>{body}</Link>;
}

export default function StartPage() {
  const [d, setD] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/public/start`).then((r) => r.json()).then(setD).catch(() => setD({ buttons: [], greeting: "Make your winning move." }));
    let sid = null;
    try { sid = sessionStorage.getItem("hm_sid"); if (!sid) { sid = Math.random().toString(36).slice(2) + Date.now().toString(36); sessionStorage.setItem("hm_sid", sid); } } catch {}
    fetch(`${API}/api/public/track`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ path: "/start/", sid, ref: document.referrer || null, w: window.innerWidth }) }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!d?.pixel_id || window.fbq) return;
    /* eslint-disable */
    !(function (f, b, e, v, n, t, s) { if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); }; if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = []; t = b.createElement(e); t.async = !0; t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s); })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
    /* eslint-enable */
    window.fbq("init", d.pixel_id); window.fbq("track", "PageView");
  }, [d?.pixel_id]);
  const { greet, line } = quip(new Date().getHours());
  const rate = d?.rate_5yr;
  const reviews = d?.reviews?.length ? d.reviews : d?.review ? [d.review] : [];
  return (
    <div className="hm-site start-page" data-testid="start-page">
      <Helmet>
        <title>Make your winning move · Hello Mortgage Team</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="description" content="Pick the one thing you came for. We'll skip the mortgage-speak." />
        <meta name="theme-color" content="#14283b" />
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Serif+Display:ital@0;1&display=swap" rel="stylesheet" />
      </Helmet>
      <main className="start-wrap">
        <header className="start-head">
          <div className="start-top">
            <Link to="/?utm_source=instagram&utm_medium=bio&utm_content=logo" className="start-mark" aria-label="Hello Mortgage home" onClick={() => tap("logo", "Logo")}><img src={LOGO_LIGHT} alt="Hello Mortgage Team" width="200" height="67" /></Link>
            <span className="start-handle" data-testid="start-handle">@hellomortgage.ca</span>
          </div>
          <div className="start-hello" aria-hidden="true">hello,</div>
          <p className="start-eyebrow" data-testid="start-eyebrow">{greet && <><b>{greet}</b> </>}{line}</p>
          <div className="start-lead" data-testid="start-lead">It's time to…</div>
          <h1 className="start-h1" data-testid="start-greeting"><Headline text={d?.greeting || "Make your winning move."} /></h1>
          {d?.sub && <p className="start-sub">{d.sub}</p>}
          {rate && <div className="start-rate" data-testid="start-rate-chip"><span className="start-live" aria-hidden="true" /><span>5-yr fixed from <strong>{rate}%</strong> today</span><small>Live Alberta rates</small></div>}
        </header>
        <nav className="start-btns" aria-label="Start here" data-testid="start-buttons">
          {(d?.buttons || []).map((b, i) => <Btn key={b.id} b={b} rate={rate} i={i} />)}
        </nav>
        {(reviews.length > 0 || d?.latest_post || d?.show_checkup) && (
          <section className="start-more" aria-label="Real people, real wins">
            <div className="start-more-head">
              <h2>Turning up the heat and making loans happen</h2>
              <div className="start-more-row">
                <p>As it turns out, people love us. But we’re not ones to brag. Instead, we like to let our clients do the talking.</p>
                <img className="start-fire" src="/brand/we-got-that-fire-light.png" alt="We got that fire" width="150" height="150" />
              </div>
            </div>
            <Reviews items={reviews} />
            {d?.latest_post && (
              <Link to={UTM(`/strategy-vault/${d.latest_post.slug}/`, "latest-post")} className="start-post" onClick={() => tap("latest-post", d.latest_post.title)} data-testid="start-latest-post">
                {d.latest_post.hero_image && <img src={d.latest_post.hero_image} alt="" loading="lazy" />}
                <div className="start-post-body">
                  <small>Fresh in the Strategy Vault{d.latest_post.published_at && <> · {new Date(d.latest_post.published_at).toLocaleDateString("en-CA", { month: "short", day: "numeric" })}</>}</small>
                  <b>{d.latest_post.title}</b>
                  {d.latest_post.summary && <p>{d.latest_post.summary}</p>}
                  <span className="start-post-cta">Read it <ArrowUpRight size={15} /></span>
                </div>
              </Link>
            )}
            {d?.show_checkup && <Link to={UTM("/tools/renewal-reminder/", "renewal-reminder")} className="start-card start-card-quiet" onClick={() => tap("renewal-reminder", "Renewal reminder")} data-testid="start-checkup"><small>Already own a home?</small><b>Set a renewal reminder. We’ll reach out when it’s time to make a winning move—and potentially save some serious cash.</b><CalendarCheck size={18} /></Link>}
          </section>
        )}
        <footer className="start-foot">
          <Link to="/?utm_source=instagram&utm_medium=bio&utm_content=explore" className="start-explore" onClick={() => tap("explore", "Explore")} data-testid="start-explore">{d?.explore_label || "Explore the full site"}</Link>
          <span>Hello Mortgage Team · Powered by BRX Mortgage · <b>#WinningInLife</b></span>
          <span className="start-foot-home" data-testid="start-foot-home">Edmonton is home. Alberta is our playground.</span>
        </footer>
      </main>
    </div>
  );
}
